/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Divider, FormLabel, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { type Dayjs } from 'dayjs';
import { RRule } from 'rrule';

import type { Recurrence } from '../../../../../../Data/types';
import IconWithTooltip from '../../../../../shared/IconWithTooltip/IconWithTooltip';
import RRuleBuilder from '../components/RRuleBuilder/RRuleBuilder';
import { TimeRange } from '../components/TimeRange/TimeRange';
import useBuilderStore from '../store/builderStore';

type Props = {
  value?: Recurrence | null;
  onChange: (value: Recurrence) => void;
  isAllDay?: boolean;
  readOnly?: boolean;
  onValidationChange?: (isValid: boolean) => void;
};

const getNextOrCurrentHalfHourTime = (): Dayjs => {
  const now = dayjs();
  const minutes = now.minute();
  const remainder = minutes % 30;
  const nextMinutes = remainder === 0 ? minutes : minutes + (30 - remainder);

  const adjustedTime =
    nextMinutes === 60 ? now.startOf('hour').add(1, 'hour') : now.minute(nextMinutes).second(0).millisecond(0);

  return adjustedTime;
};

const applyTimeToDate = (date: Date, time?: Dayjs | null): Dayjs => {
  if (!time) {
    return dayjs(date).startOf('day');
  }

  return dayjs(date).hour(time.hour()).minute(time.minute());
};

const buildRecurrenceValue = (
  rrule: RRule,
  value: Recurrence | null | undefined,
  timeRange: DateRange<Dayjs> | undefined,
): Recurrence => {
  const [startTime, endTime] = timeRange ?? [];
  const startFromRule = rrule.origOptions.dtstart;
  const untilFromRule = rrule.options.until;

  const nextValue: Recurrence = {
    ...value,
    recurrencePattern: rrule.toString(),
    startDate: value?.startDate,
    endDate: value?.endDate,
    duration: value?.duration,
  };

  if (startFromRule) {
    const newStartDate = applyTimeToDate(startFromRule, startTime);
    rrule.options.dtstart = newStartDate.toDate();
    rrule.origOptions.dtstart = newStartDate.toDate();
    nextValue.startDate = newStartDate;
  }

  if (untilFromRule) {
    const newEndDate = applyTimeToDate(untilFromRule, endTime);
    rrule.options.until = newEndDate.toDate();
    rrule.origOptions.until = newEndDate.toDate();
    nextValue.endDate = newEndDate;
    if (!nextValue.startDate && rrule.origOptions.dtstart) {
      nextValue.startDate = dayjs(rrule.origOptions.dtstart);
    }
  }

  if (!untilFromRule && !rrule.origOptions.dtstart) {
    nextValue.startDate = null;
    nextValue.endDate = null;
  }

  if (startTime && endTime) {
    nextValue.duration = endTime.diff(startTime, 'minute');
  }

  nextValue.recurrencePattern = rrule.toString();
  return nextValue;
};

const RecurrenceInput = ({ value, onChange, isAllDay, readOnly, onValidationChange }: Props) => {
  const { RRuleString } = useBuilderStore();
  const [timeRangeError, setTimeRangeError] = useState<boolean>(false);
  const rruleText = value?.recurrencePattern ? RRule.fromString(value.recurrencePattern).toText() : '';
  const defaultStartDate = value?.startDate ? dayjs(value.startDate) : getNextOrCurrentHalfHourTime();
  const [timeRange, setTimeRange] = useState<DateRange<Dayjs> | undefined>([
    defaultStartDate,
    defaultStartDate.add(value?.duration ?? 30, 'minute'),
  ]);

  useEffect(() => {
    onValidationChange?.(!timeRangeError);

    if (!RRuleString || timeRangeError) {
      return;
    }

    try {
      const rrule = RRule.fromString(RRuleString);
      const nextValue = buildRecurrenceValue(rrule, value, timeRange);
      onChange(nextValue);
    } catch (error) {
      console.error('Error parsing RRule:', error);
    }
  }, [RRuleString, timeRange, timeRangeError, onValidationChange]);

  return (
    <Stack spacing={2}>
      <Divider>Event Recurrence</Divider>
      {!isAllDay && (
        <TimeRange
          startTimeLabel="Start Time"
          endTimeLabel="End Time"
          inputSize="small"
          inputVariant="outlined"
          value={timeRange}
          readOnly={readOnly}
          onChange={setTimeRange}
          onError={setTimeRangeError}
        />
      )}
      {readOnly && (
        <>
          <Stack direction="column" spacing={1}>
            <FormLabel component="legend">
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="body2">Recurrence</Typography>
                <IconWithTooltip tooltipTitle="Recurrence pattern" icon={<InfoOutlinedIcon />} />
              </Stack>
            </FormLabel>
            {rruleText && <Typography variant="body2">Occurs {rruleText}</Typography>}
          </Stack>
          {value?.exceptions && value.exceptions.length > 0 && (
            <Stack direction="column" spacing={1}>
              <FormLabel component="legend">
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2">Exceptions</Typography>
                  <IconWithTooltip tooltipTitle="Exceptions to the recurrence pattern" icon={<InfoOutlinedIcon />} />
                </Stack>
              </FormLabel>
              <List dense disablePadding>
                {value.exceptions.map((exception) => (
                  <ListItem key={exception.exceptionDate.toISOString()}>
                    <ListItemText primary={exception.exceptionDate.format('L')} />
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}
        </>
      )}
      {!readOnly && (
        <RRuleBuilder<Dayjs>
          dateAdapter={AdapterDayjs}
          showStartDate={true}
          enableYearlyInterval={false}
          datePickerInitialDate={defaultStartDate}
          rruleString={value?.recurrencePattern ? value.recurrencePattern : ''}
          inputSize="small"
          inputVariant="outlined"
        />
      )}
    </Stack>
  );
};

export default RecurrenceInput;
